import { client } from '@/core/api/client'
import type { CouponData } from '@/types/interfaces/cart'
import type { EntryVerifyResult, RegistrationRequest } from '@/types/interfaces/entry'
import type { OrderAuthData, OrderPayload, OrderStatusResponse } from '@/types/interfaces/order'
import type { ShopApiResponse } from '@/types/interfaces/shop'
import type { UserOrderShop } from '@/types/interfaces/user'

/**
 * ★계약 영역 — 메서드/경로/파라미터/바디 변경 금지.
 * 원본: up-smartorder-web/src/api/index.js (14개 엔드포인트)
 *
 * 원본은 POST 바디를 JSON.stringify로 직렬화해 보냈다. axios가 Content-Type을
 * application/json으로 고정해두었으므로 객체를 그대로 넘겨도 동일한 바디가 나간다.
 */

/** 매장·메뉴 전체 조회 */
export function getShop(companyId: string, storeCode: string, langCode: string) {
  return client.get<ShopApiResponse>(
    `/companies/${companyId}/stores/${storeCode}?langCode=${langCode}`,
  )
}

/** LINE LIFF ID 조회 — 응답 본문이 객체가 아니라 LIFF ID 문자열 자체다 (원본 App.js: `liffId: result.data`) */
export function getLiffId(companyId: string, storeCode: string) {
  return client.get<string>(`/companies/${companyId}/stores/${storeCode}/liff-id`)
}

/** 주문 저장 → 주문번호 및 PG 인증 데이터 반환 */
export function addOrder(payload: OrderPayload) {
  return client.post<OrderAuthData>('/order', payload)
}

/**
 * 포인트 전액 결제 (paymentAmount === 0).
 * 응답 본문은 암호화된 주문 ID **문자열 그 자체**다 (객체가 아님 — 원본 Bottom.payByPointOnly 동일).
 */
export function payOrderByPointOnly(request: {
  companyId: string
  storeCode: string
  orderId: string
}) {
  return client.post<string>('/sales/point-only', request)
}

/** 주문 상세 조회 */
export function getSale(companyId: string, storeCode: string, orderId: string, langCode: string) {
  return client.get(
    `/companies/${companyId}/stores/${storeCode}/sales/${orderId}?langCode=${langCode}`,
  )
}

/** 주문 상태 조회 (OrderDetail 5초 폴링) */
export function getOrderState(companyId: string, storeCode: string, orderId: string) {
  return client.get<number>(`/companies/${companyId}/stores/${storeCode}/sales/${orderId}/status`)
}

/** 사용자가 주문한 매장 목록 */
export function getOrderShopsByUser(encryptedId: string) {
  return client.get<UserOrderShop[]>(`/users/${encryptedId}/order-stores`)
}

/** 회원 포인트 조회 */
export function getUserPointByPhoneNumber(
  companyId: string,
  storeCode: string,
  phoneNumber: string,
) {
  return client.get(`/companies/${companyId}/stores/${storeCode}/users/${phoneNumber}`)
}

/** 라이트 주문 조회 — orderId만 인코딩한다 (원본 동일) */
export function getLiteOrder(
  companyId: string,
  storeCode: string,
  orderId: string,
  langCode: string,
) {
  return client.get(
    `/companies/${companyId}/stores/${storeCode}/lite-orders/${encodeURIComponent(orderId)}?langCode=${langCode}`,
  )
}

/** QR 주문 임시 저장 */
export function addQrOrder(payload: unknown) {
  return client.post('/qr-order', payload)
}

/** 이지웰페이 결제 준비 */
export function readyEzwelPay(request: { companyId: string; storeCode: string; orderId: string }) {
  return client.post<{
    next_redirect_mobile_url: string
    next_redirect_mobile_url_intent: string
  }>('/sales/ezwelpay/ready', request)
}

/** 쿠폰 조회 */
export function getCoupon(
  companyId: string,
  storeCode: string,
  couponId: string,
  itemCode: string,
) {
  return client.get<CouponData>(
    `/companies/${companyId}/stores/${storeCode}/coupons/${couponId}?itemCode=${itemCode}`,
  )
}

/** NicePay VAN 승인 */
export function approveNicepayVan(companyId: string, storeCode: string, orderId: string) {
  return client.post<{ orderId: string }>('/sales/nicepay-van/approve', {
    companyId,
    storeCode,
    orderId,
  })
}

/** 주문 승인 상태 조회 (Result 2초 폴링) */
export function getOrderStatus(companyId: string, storeCode: string, orderId: string) {
  return client.get<OrderStatusResponse>(
    `/companies/${companyId}/stores/${storeCode}/orders/${orderId}/status`,
  )
}

/**
 * QR 매장등록 검증 (신규 — 원본 14개 계약 외 추가).
 */
export function verifyEntry(licenseKey: string) {
  return client.post<EntryVerifyResult>('/entry/verify', { licenseKey })
}

/**
 * QR 매장등록 제출 (신규 — 원본 14개 계약 외 추가).
 * 응답은 verify와 동일 형태이며 status에 LOGIN_FAILED(입력값 오류)가 추가될 수 있다.
 */
export function registerStore(request: RegistrationRequest) {
  return client.post<EntryVerifyResult>('/registration', request)
}

export const api = {
  getShop,
  getLiffId,
  addOrder,
  payOrderByPointOnly,
  getSale,
  getOrderState,
  getOrderShopsByUser,
  getUserPointByPhoneNumber,
  getLiteOrder,
  addQrOrder,
  readyEzwelPay,
  getCoupon,
  approveNicepayVan,
  getOrderStatus,
  verifyEntry,
  registerStore,
}
